import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import { createTournamentUseCase } from '@/src/core/dependencies';
import { TournamentType, ModerationError } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { CreateTournamentScreen } from '@/src/presentation/screens/CreateTournamentScreen';
import { useFocusEffect } from '@react-navigation/native';
import { Stack, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { getPlanLimitMessageKey } from '@/src/core/utils/planLimitErrors';
import { logger } from '@/src/core/utils';

export default function CreateTournamentContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedType, setSelectedType] = useState<TournamentType>('single_elimination');

  const handleSetSelectedType = (type: TournamentType) => {
    setSelectedType(type);
    if (type === 'triple_elimination') {
      setMaxParticipants(4);
    }
  };
  const [maxParticipants, setMaxParticipants] = useState<number | undefined>(4);
  const [isPublic, setIsPublic] = useState(false);
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();
  const { checkAccess, limits } = useSubscription();

  // Reset form when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      setName('');
      setDescription('');
      setSelectedType('single_elimination');
      setMaxParticipants(4);
    }, [])
  );

  const handleCreate = async () => {
    if (!user) {
      platformAlert(t('common.error'), t('alerts.mustBeLoggedIn'));
      return;
    }
    if (!name.trim()) {
      platformAlert(t('common.error'), t('alerts.enterTournamentName'));
      return;
    }

    // Feature gating: check tournament type allowed
    const typeAccess = await checkAccess({ feature: 'tournament_type', tournamentType: selectedType });
    if (!typeAccess.allowed) {
      platformAlert(t('subscription.featureLocked'), t('subscription.tournamentTypeProOnly'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
      ]);
      return;
    }

    // Feature gating: check tournament limit
    const createAccess = await checkAccess({ feature: 'create_tournament' });
    if (!createAccess.allowed) {
      platformAlert(t('subscription.featureLocked'), t('subscription.tournamentLimitReached'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
      ]);
      return;
    }

    // Feature gating: check private tournament limit only when creating a private tournament
    if (!isPublic) {
      const privateAccess = await checkAccess({ feature: 'private_tournament' });
      if (!privateAccess.allowed) {
        platformAlert(t('subscription.featureLocked'), t('subscription.privateTournamentLimitReached'), [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
        ]);
        return;
      }
    }

    try {
      setCreating(true);

      const tournament = await createTournamentUseCase.execute({
        name: name.trim(),
        description: description.trim() || undefined,
        tournamentType: selectedType,
        maxParticipants: maxParticipants,
        userId: user.id,
        isPublic,
      });
      platformAlert(t('common.success'), t('tournaments.tournamentCreated'), [
        { text: t('common.ok'), onPress: () => router.replace(`/tournament/${tournament.id}`) },
      ]);
    } catch (error) {
      const planLimitKey = getPlanLimitMessageKey(error);
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else if (planLimitKey) {
        platformAlert(t('subscription.featureLocked'), t(planLimitKey), [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
        ]);
      } else {
        logger.error('CreateTournament', 'Failed to create tournament', { error });
        platformAlert(t('common.error'), t('tournaments.failedToCreate'));
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: t('tournaments.createTournament') }} />
      <CreateTournamentScreen
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        selectedType={selectedType}
        setSelectedType={handleSetSelectedType}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
        maxParticipants={maxParticipants}
        setMaxParticipants={setMaxParticipants}
        creating={creating}
        handleCreate={handleCreate}
        handleCancel={() => router.back()}
        colors={colors}
        allowedTournamentTypes={limits.allowedTournamentTypes}
        maxParticipantsPerPlan={limits.maxParticipantsPerTournament}
      />
    </>
  );
}
